import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import ReactTable from "react-table";
import moment from "moment";
import {Card, CardContent} from "@material-ui/core";
import {Link} from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import ReportDateToolbar, {
	buildReportApiFilter,
	createDefaultReportDateState,
	defaultReportFilterState,
	formatDateRangeLabel,
	formatReportMoney,
} from "components/Elements/ReportFilterToggles";

const DATE_STORAGE_KEY = "dateWorkOrderReport";

export default class WorkOrderReport extends Framework.Components.Collection {
	constructor(props) {
		let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ_ALL);
		super(props, model);
		this.request = "GET_WORK_ORDER_REPORT";

		Object.assign(this.state, defaultReportFilterState, createDefaultReportDateState(DATE_STORAGE_KEY));
	}

	handleWorkOrderModeChange = async (completedWorkOrdersOnly) => {
		await this.setState({completedWorkOrdersOnly});
		await this.getReport();
	};

	handleTimezoneModeChange = async (useLocalTimezone) => {
		await this.setState({useLocalTimezone});
		await this.getReport();
	};

	async componentDidMount() {
		window.addEventListener('beforeunload', this.handleUnload);
		await this.getReport();
	}

	handleToggleModal = (modal) => {
		this.setState({[modal]: !this.state[modal]})
	}

	handleDateChange = async (name, date) => {
		await this.setState({filterDate: moment(date).format()});
		this.handleToggleModal("showDate");
		await this.getReport();
	};

	handleCancelDateRange = () => {
		const today = moment().format("YYYY-MM-DD");
		this.setState({start_date: today, end_date: today});
		this.handleToggleModal("showDateRange");
	};

	getReport = async () => {
		await this.setState({totals: null, loading: true});
		try {
			let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ);
			this.read = this.request;
			model.filter = buildReportApiFilter(this.state);
			this.state.filter = model.filter;
			const dateRangeFormatted = formatDateRangeLabel(
				this.state.start_date,
				this.state.end_date
			);
			model.search = this.state.search;
			const response = await model.submit(null, this.read);
			this.data = response.items;
			await this.setState({
				data: this.data,
				totals: response.totals,
				dateRangeFormatted,
				key: Math.random(),
			});
			localStorage.setItem(DATE_STORAGE_KEY, this.state.filterDate);
		} finally {
			await this.setState({loading: false});
		}
	}

	getDiscountTotal = (props) => {
		const discounts =
			parseFloat(props.item_discounts ?? 0) + parseFloat(props.product_discounts ?? 0);
		const discountPct = parseFloat(props.discount_percentage ?? 0);
		let discountPercentTotal = 0;
		if (discountPct > 0) {
			const netBeforeTax =
				parseFloat(props.net_sale ?? 0) - parseFloat(props.tax_amount ?? 0);
			const trade = parseFloat(props.trade ?? 0);
			const divisor = 1 - discountPct / 100;
			if (divisor > 0) {
				const preDiscountSubtotal = (netBeforeTax + trade) / divisor;
				discountPercentTotal = preDiscountSubtotal * (discountPct / 100);
			}
		}
		const total =
			discounts + discountPercentTotal + parseFloat(props.discount_amount ?? 0);
		return Number.isFinite(total) ? total.toFixed(2) : "0.00";
	}

	getNetSale = (r) => {
		return parseFloat(r.net_sale) - parseFloat(r.tax_amount);
	}

	getCost = (r) => {
		return (
			parseFloat(r.product_cost ?? 0.00) +
			parseFloat(r.item_cost ?? 0.00) +
			parseFloat(r.service_cost ?? 0.00)
		).toFixed(2);
	}

	fetchData = async (state) => {
		this.setState({loading: true});
		window.setTimeout(() => {
			this.fetchDataActual(state, ['totals']);
		}, 100);
	}

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.handleUnload);
		localStorage.removeItem('table-preferences-WorkOrderReport');
	}

	handleUnload = async () => {
		localStorage.removeItem('table-preferences-WorkOrderReport');
	}

	render() {
		const {data, pages, loading, limit} = this.state;
		let columns = [
			{
				Header: "Date Time",
				id: "date",
				width: 180,
				accessor: d => {
					return moment(d.created).local().format("MM/DD/YYYY hh:mm A")
				},
				sortable: false
			},
			{
				Header: "Cost",
				id: "cost",
				className: 'right-column',
				accessor: r => this.getCost(r),
				sortable: false
			},
			{
				Header: "Gross Sale",
				id: "gross_sale",
				className: 'right-column',
				accessor: r => (parseFloat(this.getNetSale(r)) + parseFloat(this.getDiscountTotal(r))).toFixed(2),
				sortable: false
			},
			{
				Header: "Discounts",
				id: "discounts",
				className: 'right-column',
				accessor: r => this.getDiscountTotal(r),
				sortable: false
			},
			{
				Header: "Returns",
				id: "refund_amount",
				className: 'right-column',
				accessor: r => r.refund_amount ?? 0.00,
				sortable: false
			},
			{
				Header: "NET Sale",
				id: "net_sale",
				className: 'right-column',
				accessor: r => parseFloat(this.getNetSale(r)).toFixed(2),
				sortable: false
			},
			{
				Header: "Tax",
				id: "tax_amount",
				className: 'right-column',
				accessor: r => r.tax_amount,
				sortable: false
			},
			{
				Header: "NET Profit",
				id: "net_profit",
				className: 'right-column',
				accessor: r => (parseFloat(this.getNetSale(r)) - parseFloat(this.getCost(r)) - (parseFloat(r.refund_amount ?? 0.00))).toFixed(2),
				sortable: false
			},
			{
				Header: "Method",
				id: "payment_method",
				className: 'center-column',
				accessor: r => r.payment_method,
				sortable: false
			},
			{
				Header: "Customer",
				id: "client",
				className: 'center-column',
				accessor: r => r.client,
				sortable: false
			},
			{
				Header: "Order #",
				id: "pkey",
				className: 'center-column',
				sortable: false,
				accessor: (r) => r.work_order_pkey ?? r.pkey,
				Cell: (props) => {
					const row = props.original;
					const id = row.work_order_pkey ?? row.pkey;
					return (
						<span>
							<Link to={`/work-orders/${id}`}>{id}</Link>
						</span>
					);
				}
			},
			{
				Header: "Items",
				id: "items",
				accessor: r => r.item_names !== null && r.product_names !== null ? (r.item_names + ', ' + r.product_names) : (r.item_names !== null ? r.item_names : r.product_names),
				sortable: false
			},
			{
				Header: "Seller",
				id: "seller",
				accessor: r => r.seller !== "" ? r.seller : r.account_name,
				sortable: false
			},
			{
				Header: "Salesman",
				id: "salesman",
				accessor: r => r.salesman,
				sortable: false
			},
		];

		return (
			<Card>
				<CardContent>
					<div className="table-bar">
						<ReportDateToolbar
							filterDate={this.state.filterDate}
							start_date={this.state.start_date}
							end_date={this.state.end_date}
							dateRangeFormatted={this.state.dateRangeFormatted}
							showDate={this.state.showDate}
							showStartDate={this.state.showStartDate}
							showEndDate={this.state.showEndDate}
							showDateRange={this.state.showDateRange}
							onToggleModal={this.handleToggleModal}
							onSingleDateChange={this.handleDateChange}
							onStartDateChange={(start_date) => this.setState({start_date})}
							onEndDateChange={(end_date) => this.setState({end_date})}
							onApplyDateRange={this.getReport}
							onCancelDateRange={this.handleCancelDateRange}
							toggleClose={this.toggleClose}
							showLayawayToggle={false}
							completedWorkOrdersOnly={this.state.completedWorkOrdersOnly}
							useLocalTimezone={this.state.useLocalTimezone}
							onWorkOrderModeChange={this.handleWorkOrderModeChange}
							onTimezoneModeChange={this.handleTimezoneModeChange}
						/>
						<div className="d-flex" style={{paddingTop: 15}}>
							<TextField
								placeholder={"0.00"}
								label={'Total Card Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_card) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Cash Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_cash) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Terminal Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_terminal) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Gift Cert Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_gift) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Check Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_check) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Zelle Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_zelle) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Invoice Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_invoice) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Amount Collected'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_payments) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							{this.renderHeaderRight("Work Order Report", columns)}
						</div>
					</div>
					{!loading && (data === 'undefined' || data[0]?.pkey === null) ?
						<div>
							There is no data for the dates selected.
						</div>
						:
					<ReactTable
						key={this.state.key}
						columns={columns}
						manual
						data={data}
						pages={pages}
						loading={loading}
						loadingText="Requesting Data"
						onFetchData={this.fetchData}
						defaultSorted={[
							{
								id: this.model.sort,
								desc: this.model.order === 'ascending'
							}
						]}
						className="-striped -highlight"
						showPaginationTop={false}
						showPaginationBottom={false}
						showPageSizeOptions={true}
						defaultPageSize={limit}
					/>
					}
					<Framework.Elements.LoadingSpinner isLoading={loading} />
				</CardContent>
			</Card>
		)
	}
}
